import { useMemo } from 'react';
import { diffWords, diffJson } from 'diff';
import Box from '@mui/material/Box';
import { alpha, type Theme } from '@mui/material/styles';
import { stripHtml } from './diffStats';

/**
 * Two versions of something, with what changed in colour. Two shapes sharing
 * one surface and one pair of tints:
 *
 * - {@link DiffView} compares TEXT word by word — a Bemerkung rewritten in the
 *   Verlauf, a field in conflict.
 * - {@link JsonDiffView} compares two RECORDS line by line. Words are the wrong
 *   unit there: a record is a list of fields, and the answer wanted is which
 *   lines are not in both.
 */

/** The ground a diff sits on, one step off the surrounding paper in EITHER mode. */
const surfaceSx = {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    p: 1.5,
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    // `grey.50` was a light-mode constant that turned into a glaring white
    // block after dark; tinted from the text colour, it follows the theme.
    bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04)
};

/**
 * Tinted from the palette rather than fixed pastels, so both stay legible in
 * light and dark.
 */
const addedSx = {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.22),
    color: 'success.main'
};

const removedSx = {
    bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.22),
    color: 'error.main'
};

interface DiffViewProps {
    oldValue: string;
    newValue: string;
    isHtml?: boolean;
}

export default function DiffView({
    oldValue,
    newValue,
    isHtml
}: DiffViewProps) {
    const oldText = isHtml ? stripHtml(oldValue) : oldValue;
    const newText = isHtml ? stripHtml(newValue) : newValue;

    if (oldText === newText) {
        return null;
    }

    const parts = diffWords(oldText, newText);

    return (
        <Box sx={surfaceSx}>
            {parts.map((part, index) => {
                if (part.added) {
                    return (
                        <Box
                            component="span"
                            key={index}
                            sx={{ ...addedSx, textDecoration: 'none' }}
                        >
                            {part.value}
                        </Box>
                    );
                }
                if (part.removed) {
                    return (
                        <Box
                            component="span"
                            key={index}
                            sx={{
                                ...removedSx,
                                textDecoration: 'line-through'
                            }}
                        >
                            {part.value}
                        </Box>
                    );
                }
                return <span key={index}>{part.value}</span>;
            })}
        </Box>
    );
}

interface DiffLine {
    key: string;
    text: string;
    state: 'added' | 'removed' | 'same';
}

/**
 * The lines of a record diff, as `diff` reports them.
 *
 * `diffJson` sorts the keys of both objects before comparing, so two records
 * differing only in field order come out identical; a textual diff of
 * `JSON.stringify` would paint the whole record red and green. A change arrives
 * as a removed part immediately followed by an added one, which is how it
 * should be read, so the parts are flattened in order.
 */
function toLines(oldValue: unknown, newValue: unknown): DiffLine[] {
    const parts = diffJson(
        (oldValue ?? {}) as object,
        (newValue ?? {}) as object
    );
    const lines: DiffLine[] = [];
    for (const [index, part] of parts.entries()) {
        const state = part.added ? 'added' : part.removed ? 'removed' : 'same';
        // Every part ends with a newline, so the split leaves an empty tail.
        const texts = part.value.split('\n');
        if (texts[texts.length - 1] === '') {
            texts.pop();
        }
        for (const [line, text] of texts.entries()) {
            lines.push({ key: `${index}-${line}`, text, state });
        }
    }
    return lines;
}

const MARKER: Record<DiffLine['state'], string> = {
    added: '+',
    removed: '−',
    same: ' '
};

/**
 * Two versions of one record, line by line. Red is what is there now, green is
 * what the file would write — the direction every diff is read in, and the one
 * the import's own wording follows.
 *
 * Unchanged lines stay in place: a record is short enough to read whole, and a
 * changed field means nothing without the Befundnummer three lines above it.
 */
export function JsonDiffView({
    oldValue,
    newValue
}: {
    /** The current version — the server's. Rendered as the removed side. */
    oldValue: unknown;
    /** The incoming version — the file's. Rendered as the added side. */
    newValue: unknown;
}) {
    const lines = useMemo(
        () => toLines(oldValue, newValue),
        [oldValue, newValue]
    );

    return (
        <Box sx={{ ...surfaceSx, p: 0, overflow: 'auto', maxHeight: 420 }}>
            {lines.map((line) => (
                <Box
                    key={line.key}
                    sx={{
                        display: 'flex',
                        gap: 1,
                        px: 1.5,
                        ...(line.state === 'added' ? addedSx : {}),
                        ...(line.state === 'removed' ? removedSx : {})
                    }}
                >
                    <Box
                        component="span"
                        aria-hidden
                        sx={{ userSelect: 'none', opacity: 0.7 }}
                    >
                        {MARKER[line.state]}
                    </Box>
                    <Box component="span" sx={{ flex: 1 }}>
                        {line.text}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
